import { Box, Theme, useMediaQuery } from '@mui/material';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ALLOWED_LINEUP_EMBED_ORIGINS } from 'constants/embed.constants';

import LoadingScreen from 'components/@common/LoadingScreen';

interface ResizeMessage {
  type: 'RESIZE';
  height: number;
}

interface ParentScrollMessage {
  type: 'PARENT_SCROLL';
  scrollY: number;
}

interface MdlbeastEmbedProps {
  path: string;
  title?: string;
}

const isValidOrigin = (origin: string): boolean => {
  return ALLOWED_LINEUP_EMBED_ORIGINS.includes(
    origin as (typeof ALLOWED_LINEUP_EMBED_ORIGINS)[number],
  );
};

const MdlbeastEmbed: React.FC<MdlbeastEmbedProps> = ({
  path,
  title = 'MDLBEAST Embed',
}) => {
  const router = useRouter();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState('100vh');
  const [isIframeLoading, setIsIframeLoading] = useState(true);

  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const locale = router.locale || 'en';

  const EMBED_URL = process.env.NEXT_PUBLIC_LINEUP_EMBED_URL;
  const src = `${EMBED_URL}/${locale}/events/${path}/embed/web`;

  // Function to send scroll information to iframe
  const sendScrollToIframe = useCallback((iframe: HTMLIFrameElement | null) => {
    if (!iframe || !iframe.contentWindow) return;

    // Send initial scroll position
    iframe.contentWindow.postMessage(
      {
        type: 'PARENT_SCROLL',
        scrollY: window.scrollY,
      } as ParentScrollMessage,
      '*',
    );
  }, []);

  // Debounced version of sendScrollToIframe
  const debouncedSendScrollToIframe = useRef<NodeJS.Timeout | null>(null);
  const sendScrollToIframeDebounced = useCallback(
    (iframe: HTMLIFrameElement | null) => {
      if (debouncedSendScrollToIframe.current) {
        clearTimeout(debouncedSendScrollToIframe.current);
      }
      debouncedSendScrollToIframe.current = setTimeout(() => {
        sendScrollToIframe(iframe);
      }, 100);
    },
    [sendScrollToIframe],
  );

  // Listen for postMessage events from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isValidOrigin(event.origin)) {
        console.warn('Ignored message from unknown origin:', event.origin);
        return;
      }

      const { type } = event.data || {};

      switch (type) {
        case 'RESIZE': {
          const message = event.data as ResizeMessage;
          if (typeof message.height === 'number') {
            setIframeHeight(`${message.height}px`);
          }
          break;
        }

        case 'SCROLL_TO_DIALOG': {
          const iframe = iframeRef.current;
          if (!iframe) return;

          const rect = iframe.getBoundingClientRect();
          const targetScrollY = isMobile
            ? window.scrollY + rect.top + rect.height - window.innerHeight
            : window.scrollY +
              rect.top -
              window.innerHeight / 2 +
              rect.height / 2;

          window.scrollTo({
            top: Math.max(targetScrollY, 0),
            behavior: 'instant',
          });
          break;
        }

        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isMobile]);

  /**
   * below useEffect is to handle the child iframe mui dialog scrolling when using dynamic height
   */
  useEffect(() => {
    // Store original scroll behavior
    const originalScrollBehavior =
      document.documentElement.style.scrollBehavior;
    const originalBodyScrollBehavior = document.body.style.scrollBehavior;

    // Force instant scroll behavior
    document.documentElement.style.scrollBehavior = 'auto';
    document.body.style.scrollBehavior = 'auto';

    return () => {
      // Restore original scroll behavior
      document.documentElement.style.scrollBehavior = originalScrollBehavior;
      document.body.style.scrollBehavior = originalBodyScrollBehavior;
    };
  }, []);

  // Set up scroll event listener to send scroll data to iframe
  useEffect(() => {
    const handleScroll = () => {
      sendScrollToIframeDebounced(iframeRef.current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (debouncedSendScrollToIframe.current) {
        clearTimeout(debouncedSendScrollToIframe.current);
      }
    };
  }, [sendScrollToIframeDebounced]);

  // TODO: onLoad and onError does not seem to work for some reason
  // Loader timeout: show loader for 3 seconds, then show iframe
  useEffect(() => {
    setIsIframeLoading(true);

    if (!path) {
      return;
    }
    const timeout = setTimeout(() => {
      setIsIframeLoading(false);
    }, 3000);
    return () => clearTimeout(timeout);
  }, [src, path]);

  if (!path) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      {isIframeLoading && <LoadingScreen />}
      <iframe
        ref={iframeRef}
        src={src}
        width="100%"
        height={iframeHeight}
        title={title}
        style={{
          width: '100%',
          height: iframeHeight,
          border: 'none',
          transition: 'height 0.3s ease-in-out',
          overflow: 'hidden',
          display: isIframeLoading ? 'none' : 'block',
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
      />
    </Box>
  );
};

export default MdlbeastEmbed;
